import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import SchemaMarkup from "@/components/SchemaMarkup";
import { organizationSchema, localBusinessSchema, websiteSchema } from "@/data/schemas";

// Hard-reload shim: forces a full browser navigation so the server serves the
// hand-authored static HTML file at /public/locations/oakville/index.html
// instead of the React SPA's dynamic LocationPage template.
const StaticHTMLReload = () => {
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.location.replace(window.location.pathname);
    }
  }, []);
  return null;
};

// Landing + marketing chrome are lazy too, so the shared main.js stays minimal
// for the admin / trainer / client apps, which never render them.
const HomePage = lazy(() => import("@/pages/HomePageNew"));

// Every other page is lazy-loaded → its own chunk, keeping main.js tiny.
// React routes are client-rendered in prod regardless (crawlers get the
// hand-authored static shells), so lazy-loading costs no SEO.
const AboutPage = lazy(() => import("@/pages/AboutPageNew"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ProgramPage = lazy(() => import("@/pages/ProgramPage"));
const CorporatePage = lazy(() => import("@/pages/CorporatePage"));
const WorkWithUsPage = lazy(() => import("@/pages/WorkWithUsPage"));
const SouthAsianHealthPage = lazy(() => import("@/pages/SouthAsianHealthPage"));
const BookAssessmentPage = lazy(() => import("@/pages/BookAssessmentPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const KWCHubPage = lazy(() => import("@/pages/KWCPages").then(m => ({ default: m.KWCHubPage })));
const KitchenerPage = lazy(() => import("@/pages/KWCPages").then(m => ({ default: m.KitchenerPage })));
const WaterlooPage = lazy(() => import("@/pages/KWCPages").then(m => ({ default: m.WaterlooPage })));
const LocationPage = lazy(() => import("@/pages/LocationPage"));
const LocationsMainPage = lazy(() => import("@/pages/LocationsMainPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const ClientResultsPage = lazy(() => import("@/pages/ClientResultsPage"));
const WhyTrainWithUsPage = lazy(() => import("@/pages/WhyTrainWithUsPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const SeniorWeightTrainingEtobicokePage = lazy(() => import("@/pages/SeniorWeightTrainingEtobicokePage"));

// Lazy-loaded — portals, admin, payment flows, onboarding, calculators
// These are only seen by authenticated/post-action users and bundle out into separate chunks.
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const BellyFatInsightPage = lazy(() => import("@/pages/BellyFatInsightPage"));
const BodyFatCalculatorPage = lazy(() => import("@/pages/BodyFatCalculatorPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));
const TrainerPortalPage = lazy(() => import("@/pages/TrainerPortalPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelledPage = lazy(() => import("@/pages/PaymentCancelledPage"));
const TrainerOnboardingPage = lazy(() => import("@/pages/TrainerOnboardingPage"));
const SignupLinkPage = lazy(() => import("@/pages/SignupLinkPage"));
const ParQPublicPage = lazy(() => import("@/pages/ParQPublicPage"));const ConsultPage = lazy(() => import("@/pages/ConsultPage"));
const InjuryScreeningPage = lazy(() => import("@/pages/InjuryScreeningPage"));
const CityProgramMatrixPage = lazy(() => import("@/pages/CityProgramMatrixPage"));
const CoveragePage = lazy(() => import("@/pages/CoveragePage"));

import ScrollToTop from "@/components/ScrollToTop";
const Navigation = lazy(() => import("@/components/Navigation"));
const Footer = lazy(() => import("@/components/Footer"));
const Chatbot = lazy(() => import("@/components/Chatbot"));
const InstallPrompt = lazy(() => import("@/components/InstallPrompt"));
const WhatsAppWidget = lazy(() => import("@/components/WhatsAppWidget"));


// Minimal loading state for lazy chunks — forest green spinner matches brand
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-white" data-testid="route-loading">
      <div className="h-8 w-8 rounded-full border-2 border-[#1A3529] border-t-transparent animate-spin" aria-label="Loading" />
    </div>
  );
}

function AppLayout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isPortalRoute = location.pathname.startsWith("/portal");
  const isTrainerRoute = location.pathname.startsWith("/trainer");
  const isConsultRoute = location.pathname.startsWith("/consult");
  const isParqRoute = location.pathname.startsWith("/parq");
  const hideChrome = isAdminRoute || isPortalRoute || isTrainerRoute || isConsultRoute || isParqRoute;

  useEffect(() => {
    if (typeof window !== "undefined" && window.fbq) window.fbq("track", "PageView");
  }, [location.pathname]);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    let manifest = "/manifest.json";
    if (isPortalRoute) manifest = "/manifest-client.json";
    else if (isTrainerRoute) manifest = "/manifest-trainer.json";
    else if (isAdminRoute) manifest = "/manifest-admin.json";
    link.setAttribute("href", manifest);

    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) {
      if (isTrainerRoute) themeMeta.setAttribute("content", "#B91C1C");
      else if (isAdminRoute) themeMeta.setAttribute("content", "#C8973A");
      else themeMeta.setAttribute("content", "#1A3529");
    }

    const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleIcon) {
      if (isTrainerRoute) appleIcon.setAttribute("href", "/icon-trainer-192.png");
      else if (isAdminRoute) appleIcon.setAttribute("href", "/icon-admin-192.png");
      else appleIcon.setAttribute("href", "/icon-192x192.png");
    }
  }, [location.pathname, isAdminRoute, isPortalRoute, isTrainerRoute]);

  const pwaRole = isAdminRoute ? "admin" : isTrainerRoute ? "trainer" : isPortalRoute ? "client" : null;

  return (
    <>
      {!hideChrome && (
        <SchemaMarkup schema={[organizationSchema(), localBusinessSchema(), websiteSchema()]} />
      )}
      {!hideChrome && (
        <Suspense fallback={null}><Navigation /></Suspense>
      )}
      {children}
      <Suspense fallback={null}>
        {!hideChrome && <Footer />}
        {!hideChrome && <Chatbot />}
        {!hideChrome && <WhatsAppWidget />}
        {pwaRole && <InstallPrompt role={pwaRole} />}
      </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-white">
      <BrowserRouter>
        <ScrollToTop />
        <AppLayout>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* === NEW SITE ARCHITECTURE === */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/programs" element={<ServicesPage />} />
            {/* Program slug redirects MUST precede the generic /:slug route */}
            <Route path="/programs/transformation" element={<Navigate to="/programs/transformation-system" replace />} />
            <Route path="/programs/group-training" element={<Navigate to="/programs/partner-training" replace />} />
            {/* === Phase 11 program-slug renames + merges (301 → new canonical) === */}
            <Route path="/programs/personal-training"       element={<Navigate to="/programs/precision-training" replace />} />
            <Route path="/programs/return-to-function"      element={<Navigate to="/programs/post-surgical-strength" replace />} />
            <Route path="/programs/chronic-pain"            element={<Navigate to="/programs/persistent-pain" replace />} />
            <Route path="/programs/pelvic-floor"            element={<Navigate to="/programs/core-pelvic-floor" replace />} />
            <Route path="/programs/hypertension"            element={<Navigate to="/programs/heart-blood-pressure" replace />} />
            <Route path="/programs/postnatal"               element={<Navigate to="/programs/postnatal-recovery" replace />} />
            <Route path="/programs/seniors"                 element={<Navigate to="/programs/active-aging" replace />} />
            <Route path="/programs/diabetes"                element={<Navigate to="/programs/metabolic-health" replace />} />
            <Route path="/programs/pre-diabetes"            element={<Navigate to="/programs/metabolic-health" replace />} />
            <Route path="/programs/bone-health"             element={<Navigate to="/programs/bone-density" replace />} />
            <Route path="/programs/post-cardiac"            element={<Navigate to="/programs/heart-blood-pressure" replace />} />
            <Route path="/programs/athletic-conditioning"   element={<Navigate to="/programs" replace />} />
            <Route path="/programs/:slug" element={<ProgramPage />} />
            {/* B2B injury screening (Partner With Us track) */}
            <Route path="/partners/injury-screening" element={<InjuryScreeningPage />} />
            <Route path="/corporate" element={<CorporatePage />} />
            <Route path="/work-with-us" element={<Navigate to="/partner-with-us" replace />} />
            <Route path="/partner-with-us" element={<WorkWithUsPage />} />
            <Route path="/south-asian-health" element={<SouthAsianHealthPage />} />
            <Route path="/weight-training-for-seniors-etobicoke" element={<SeniorWeightTrainingEtobicokePage />} />
            <Route path="/book-assessment" element={<BookAssessmentPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />

            {/* KWC dedicated pages */}
            <Route path="/kitchener-waterloo" element={<KWCHubPage />} />
            <Route path="/kitchener-personal-trainer" element={<KitchenerPage />} />
            <Route path="/waterloo-personal-trainer" element={<WaterlooPage />} />

            {/* Short city URLs → direct to destination pages (no redirect chain) */}
            <Route path="/brampton"     element={<Navigate to="/brampton/senior-personal-training" replace />} />
            <Route path="/mississauga"  element={<Navigate to="/mississauga/senior-in-home-personal-trainer" replace />} />
            <Route path="/oakville"     element={<Navigate to="/locations/oakville" replace />} />
            <Route path="/toronto"      element={<Navigate to="/locations/downtown-toronto" replace />} />
            <Route path="/markham"      element={<Navigate to="/locations/markham" replace />} />
            <Route path="/vaughan"      element={<Navigate to="/locations/vaughan" replace />} />
            <Route path="/milton"       element={<Navigate to="/locations/milton" replace />} />

            {/* === REMOVED HUB PAGES → 301 to destination sub-service pages === */}
            {/* /locations/oakville is INTENTIONALLY preserved and NOT redirected. */}
            <Route path="/locations/brampton" element={<Navigate to="/brampton/senior-personal-training" replace />} />
            <Route path="/locations/mississauga" element={<Navigate to="/mississauga/senior-in-home-personal-trainer" replace />} />
            <Route path="/locations/burlington" element={<Navigate to="/locations/burlington/in-home-personal-trainer" replace />} />

            {/* Legacy location slug redirects (removed slugs → nearest valid city) */}
            <Route path="/locations/toronto" element={<Navigate to="/locations/downtown-toronto" replace />} />
            <Route path="/locations/hamilton" element={<Navigate to="/locations/burlington/in-home-personal-trainer" replace />} />
            <Route path="/locations/barrie" element={<Navigate to="/locations/newmarket" replace />} />

            {/* === LEGACY 301 REDIRECTS === */}
            <Route path="/1-on-1-personal-training" element={<Navigate to="/programs/precision-training" replace />} />
            <Route path="/in-home-personal-training" element={<Navigate to="/programs/precision-training" replace />} />
            <Route path="/partner-training" element={<Navigate to="/programs/partner-training" replace />} />
            <Route path="/belly-fat-loss-program" element={<Navigate to="/programs/transformation-system" replace />} />
            <Route path="/back-pain-mobility-training" element={<Navigate to="/programs/injury-prevention" replace />} />
            <Route path="/postnatal-fitness" element={<Navigate to="/programs/postnatal-recovery" replace />} />
            <Route path="/senior-fitness" element={<Navigate to="/programs/active-aging" replace />} />
            <Route path="/womens-fitness" element={<Navigate to="/programs/womens-health" replace />} />
            <Route path="/virtual-training" element={<Navigate to="/programs/precision-training" replace />} />
            <Route path="/book" element={<Navigate to="/book-assessment" replace />} />

            {/* Legacy /personal-trainer-* → direct to destinations (no redirect chains) */}
            <Route path="/personal-trainer-brampton" element={<Navigate to="/brampton/senior-personal-training" replace />} />
            <Route path="/personal-trainer-mississauga" element={<Navigate to="/mississauga/senior-in-home-personal-trainer" replace />} />

            {/* Legacy 2-segment orphan URLs (pre /locations/ move) → destinations under /locations/{city}/{service}.
                serve.json includes CDN-level 301s but the CDN is not honoring them in production, so we also
                do a React Router-level redirect. Googlebot executes JS and will follow this to the new URL. */}
            <Route path="/brampton/senior-personal-training" element={<StaticHTMLReload />} />
            <Route path="/brampton/women-personal-trainer" element={<Navigate to="/locations/brampton/women-personal-trainer" replace />} />
            <Route path="/mississauga/senior-in-home-personal-trainer" element={<StaticHTMLReload />} />
            <Route path="/personal-trainer-milton" element={<Navigate to="/locations/milton" replace />} />
            <Route path="/personal-trainer-kitchener" element={<Navigate to="/kitchener-personal-trainer" replace />} />
            <Route path="/personal-trainer-waterloo" element={<Navigate to="/waterloo-personal-trainer" replace />} />
            <Route path="/personal-trainer-cambridge" element={<Navigate to="/locations/cambridge" replace />} />
            <Route path="/personal-trainer-oakville" element={<Navigate to="/locations/oakville" replace />} />
            <Route path="/personal-trainer-toronto" element={<Navigate to="/locations/downtown-toronto" replace />} />

            {/* Existing city/location pages (keep) */}
            <Route path="/locations" element={<LocationsMainPage />} />
            {/* Hand-authored static SEO landing — bypasses React and serves /public/locations/oakville/index.html */}
            <Route path="/locations/oakville" element={<StaticHTMLReload />} />
            <Route path="/locations/markham" element={<StaticHTMLReload />} />
            <Route path="/locations/downtown-toronto" element={<StaticHTMLReload />} />
            <Route path="/locations/cambridge" element={<StaticHTMLReload />} />
            <Route path="/locations/king-city" element={<StaticHTMLReload />} />

            {/* Hand-authored static sub-service pages under /public/locations/{city}/{service}/index.html
                — client-side navigation from the SPA (footer clicks, in-page links) has no matching React
                route for these 2-segment paths, so we force a full-page reload which lets the CDN serve
                the static HTML directly. Direct URL type-ins already work; this fixes the footer/link path. */}
            <Route path="/locations/brampton/women-personal-trainer" element={<StaticHTMLReload />} />
            <Route path="/locations/brampton/senior-personal-training" element={<Navigate to="/brampton/senior-personal-training" replace />} />
            <Route path="/locations/burlington/in-home-personal-trainer" element={<StaticHTMLReload />} />
            <Route path="/locations/burlington/women-personal-trainer" element={<StaticHTMLReload />} />
            <Route path="/locations/milton/in-home-personal-trainer" element={<StaticHTMLReload />} />
            <Route path="/locations/milton/women-personal-trainer" element={<StaticHTMLReload />} />
            <Route path="/mississauga/senior-in-home-personal-trainer" element={<Navigate to="/mississauga/senior-in-home-personal-trainer" replace />} />
            <Route path="/locations/mississauga/women-personal-trainer" element={<StaticHTMLReload />} />
            <Route path="/locations/mississauga/in-home-personal-training" element={<StaticHTMLReload />} />
            <Route path="/locations/oakville/women-personal-trainer" element={<StaticHTMLReload />} />
            <Route path="/personal-trainer-that-comes-to-you-ontario" element={<StaticHTMLReload />} />
            <Route path="/locations/:city" element={<LocationPage />} />
            <Route path="/coverage" element={<CoveragePage />} />
            <Route path="/pt/:city/:program" element={<CityProgramMatrixPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/client-results" element={<ClientResultsPage />} />
            <Route path="/results" element={<Navigate to="/client-results" replace />} />
            <Route path="/transformations" element={<Navigate to="/client-results" replace />} />
            <Route path="/why-train-with-us" element={<WhyTrainWithUsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            {/* Ad-funnel landing pages — hand-authored static HTML, noindex, off-map from React SPA */}
            <Route path="/phase1" element={<StaticHTMLReload />} />
            <Route path="/phase1-step2" element={<StaticHTMLReload />} />
            {/* Hand-authored static blog posts — bypass React and serve /public/blog/{slug}/index.html */}
            <Route path="/blog/pcos-hormonal-health-training-south-asian-women-brampton" element={<StaticHTMLReload />} />
            <Route path="/blog/postnatal-recovery-hormonal-reset-south-asian-women-brampton" element={<StaticHTMLReload />} />
            <Route path="/blog/registered-kinesiologist-vs-personal-trainer-mississauga" element={<StaticHTMLReload />} />
            <Route path="/blog/kinesiology-tax-deduction-mississauga" element={<StaticHTMLReload />} />
            <Route path="/blog/fall-prevention-mississauga-seniors" element={<StaticHTMLReload />} />
            <Route path="/blog/diabetes-exercise-seniors-mississauga" element={<StaticHTMLReload />} />
            <Route path="/blog/gentle-strength-training-arthritis-joint-pain-mississauga" element={<StaticHTMLReload />} />
            <Route path="/blog/cardiac-safe-exercise-seniors-mississauga" element={<StaticHTMLReload />} />
            <Route path="/blog/registered-kinesiologist-vs-personal-trainer-markham" element={<StaticHTMLReload />} />
            <Route path="/blog/personal-trainer-markham-cost" element={<StaticHTMLReload />} />
            <Route path="/blog/exercise-for-type-2-diabetes-prediabetes-markham" element={<StaticHTMLReload />} />
            <Route path="/blog/fall-prevention-exercises-markham-seniors" element={<StaticHTMLReload />} />
            <Route path="/blog/strength-training-desk-workers-markham" element={<StaticHTMLReload />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/insights/belly-fat" element={<BellyFatInsightPage />} />
            <Route path="/insights/body-fat-calculator" element={<BodyFatCalculatorPage />} />
            {/* Legacy /meal-prep redirect (page retired) → closest equivalent */}
            <Route path="/meal-prep" element={<Navigate to="/programs/transformation-system" replace />} />

            {/* Reserved generic ServicePage routes (for SEO) */}
            <Route path="/service/:slug" element={<ServicePage />} />

            {/* Booking flow (kept under new URL) */}
            <Route path="/booking" element={<BookingPage />} />

            {/* === PORTALS === */}
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/portal" element={<ClientPortalPage />} />
            <Route path="/trainer" element={<TrainerPortalPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
            <Route path="/trainer-onboarding" element={<TrainerOnboardingPage />} />
            <Route path="/signup/:linkId" element={<SignupLinkPage />} />
            <Route path="/parq/:token" element={<ParQPublicPage />} />
            <Route path="/consult" element={<ConsultPage />} />
          </Routes>
          </Suspense>
        </AppLayout>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
